import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Fulfillment } from '@/types/fulfillment';
import { Rocket, Calendar, Users, Coins } from 'lucide-react';
import { motion } from 'framer-motion';
import GlassPill from '@/components/ui/GlassPill';
import { format } from 'date-fns';

interface FulfillmentCardProps {
  project: Fulfillment;
  onClick: (project: Fulfillment) => void;
  columnId?: string;
}

const FulfillmentCard = ({ project, onClick, columnId }: FulfillmentCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: project.id,
    data: {
      type: 'project',
      project,
      columnId
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 1,
  };

  // Format date to be more readable
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (e) {
      return dateString;
    }
  };

  // Format budget to show as currency
  const formatBudget = (budget?: number) => {
    if (!budget && budget !== 0) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(budget);
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="cursor-grab active:cursor-grabbing bg-white/50 dark:bg-slate-800/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50 p-4 mb-3 shadow-sm transition-all duration-200 select-none"
      onClick={() => onClick(project)}
      data-project-id={project.id}
      data-column-id={columnId}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Rocket size={16} className="text-accent" />
            <h3 className="font-medium text-lg">
              {project.title || project.name || 'Unnamed Project'}
            </h3>
          </div>
        </div>

        <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
          <span className="font-medium">{project.project_type}</span>
        </div>

        {project.project_manager && (
          <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
            <Users size={14} className="flex-shrink-0" />
            <p className="truncate">Manager: {project.project_manager}</p>
          </div>
        )}

        {project.target_completion_date && (
          <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
            <Calendar size={14} className="flex-shrink-0" />
            <p className="truncate">Due: {formatDate(project.target_completion_date)}</p>
          </div>
        )}

        <div className="flex items-center justify-between mt-2">
          {project.project_category && (
            <div className="flex items-center">
              <GlassPill 
                type={project.project_category}
                variant="blue"
                className="text-xs"
              />
            </div>
          )}
          {project.budget && (
            <div className="flex items-center gap-1">
              <Coins size={14} className="text-gray-500" />
              <div className="bg-accent/20 text-accent rounded-full px-2 py-0.5 text-xs font-medium">
                {formatBudget(project.budget)}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default FulfillmentCard;
